import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getMailboxUnreadSummary } from '../api';

const LAST_READ_AT_KEY_PREFIX = 'dc.mailbox_read.';

function readLastReadAt(appId: string): string {
  if (typeof window === 'undefined') return new Date(0).toISOString();
  try {
    const k = `${LAST_READ_AT_KEY_PREFIX}${appId}`;
    const raw = localStorage.getItem(k);
    if (!raw) return new Date(0).toISOString();
    const d = new Date(raw);
    if (!Number.isFinite(d.getTime())) return new Date(0).toISOString();
    return d.toISOString();
  } catch {
    return new Date(0).toISOString();
  }
}

export function useMailboxUnread(appId: string) {
  const [lastReadAt, setLastReadAt] = useState(() => readLastReadAt(appId));
  const prevCountRef = useRef<number | null>(null);

  const unreadQ = useQuery({
    queryKey: ['mailbox-unread', appId, lastReadAt],
    enabled: Boolean(appId),
    queryFn: async () => {
      const r = await getMailboxUnreadSummary([appId], lastReadAt);
      const item = Array.isArray(r?.summary) ? r.summary.find((s) => s.app_id === appId) : null;
      return Number(item?.count || 0);
    },
    staleTime: 10_000,
    refetchInterval: 30_000,
  });

  const unreadCount = Number(unreadQ.data || 0);

  useEffect(() => {
    if (!unreadQ.isSuccess) return;

    // Prevent toast on initial load.
    if (prevCountRef.current === null) {
      prevCountRef.current = unreadCount;
      return;
    }

    if (unreadCount > (prevCountRef.current ?? 0)) {
      const delta = unreadCount - (prevCountRef.current ?? 0);
      toast.info(`Mailbox update: ${delta} new item${delta === 1 ? '' : 's'}`, {
        description: `App: ${appId}`,
      });
    }

    prevCountRef.current = unreadCount;
  }, [unreadCount, unreadQ.isSuccess, appId]);

  async function markAllRead() {
    const nowIso = new Date().toISOString();
    try {
      localStorage.setItem(`${LAST_READ_AT_KEY_PREFIX}${appId}`, nowIso);
    } catch {
      // ignore storage failures; UI will still update based on state
    }
    setLastReadAt(nowIso);
  }

  return { unreadCount, markAllRead, lastReadAt };
}

